// packages/shared/src/utils/distance.ts
export const EARTH_RADIUS_METERS = 6_371_000;
export const METERS_PER_MILE = 1609.344;
export const METERS_PER_FOOT = 0.3048;
export const METERS_PER_KM = 1000;
/** 0.1 mi expressed in feet — the imperial cutover point. */
const FEET_PER_TENTH_MILE = 528;
const toRadians = (deg) => (deg * Math.PI) / 180;
/**
 * Format a distance in meters for display based on locale.
 *
 * Imperial: decimal miles down to 0.1 mi, then feet.
 * Metric:   decimal kilometres down to 1 km, then metres.
 *
 * Yards are never used. They are unfamiliar to most people as a sense of
 * distance outside a sports field, and mixing them with miles gave three
 * imperial units across the app for no benefit.
 *
 * Each cutover is decided on the ROUNDED small unit rather than the raw
 * value, so the boundary cannot render as "528 ft" when that is exactly the
 * 0.1 mi it would otherwise be shown as, or "1000 m" instead of "1 km".
 */
export const formatDistance = (meters, locale = 'metric') => {
    if (locale === 'imperial') {
        const feet = Math.round(meters / METERS_PER_FOOT);
        if (feet < FEET_PER_TENTH_MILE) {
            return { value: feet, unit: 'ft', display: `${feet} ft` };
        }
        const rounded = Math.round((meters / METERS_PER_MILE) * 10) / 10;
        return { value: rounded, unit: 'mi', display: `${rounded} mi` };
    }
    const m = Math.round(meters);
    if (m < METERS_PER_KM) {
        return { value: m, unit: 'm', display: `${m} m` };
    }
    const rounded = Math.round((meters / METERS_PER_KM) * 10) / 10;
    return { value: rounded, unit: 'km', display: `${rounded} km` };
};
/**
 * Haversine great-circle distance between two lat/lng points, in meters.
 * Pure function. No external deps. Safe to use on web, RN, and server.
 */
export const haversineMeters = (a, b) => {
    const lat1 = toRadians(a.lat);
    const lat2 = toRadians(b.lat);
    const dLat = lat2 - lat1;
    const dLng = toRadians(b.lng - a.lng);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
};
/**
 * Detect whether to use imperial or metric based on user's country.
 * US, Myanmar, and Liberia use imperial; everyone else uses metric.
 *
 * GB is in the list because this function is about DISTANCE specifically: the
 * UK signs and measures road distance in miles despite being metric for almost
 * everything else. iOS agrees — it reports a distinct `uk` measurement system
 * rather than folding the UK into `metric` — so including GB here keeps the web
 * fallback and the mobile OS reading from disagreeing for a British viewer.
 */
export const detectDistanceLocale = (countryCode) => {
    const imperialCountries = ['US', 'MM', 'LR', 'GB'];
    return imperialCountries.includes(countryCode.toUpperCase()) ? 'imperial' : 'metric';
};
export const metersToMiles = (m) => m / METERS_PER_MILE;
export const metersToKilometers = (m) => m / METERS_PER_KM;
